import { error } from '@sveltejs/kit'
import type { Category, FullBrand } from '../elasticsearch/types'
import type { Schema, ValidateOptions } from 'yup'

/**
 * Like `url.searchParams.get(param_name)`, but apply a function on the value if it's set.
 */
export function getSearchParam<T>(
	url: URL,
	param_name: string,
	transform: (s: string) => T,
	default_: T,
) {
	const value = url.searchParams.get(param_name)
	if (value) {
		return transform(value)
	}
	return default_
}

export function getIntSearchParam(url: URL, param_name: string, default_: number) {
	return getSearchParam<number>(
		url,
		param_name,
		(s) => {
			if (!/^-?\d+$/.test(s)) {
				return default_
			}

			const n = parseInt(s, 10)
			return isNaN(n) ? default_ : n
		},
		default_,
	)
}

/**
 * Get brands URL parameter to filter products
 *
 * TODO: use getURLParams instead
 */
export function getBrandsParam(url: URL, param_name: string, brands: Array<FullBrand>) {
	const value = url.searchParams.get(param_name)
	if (value) {
		const brand_slugs = value?.split(',')
		const brand_ids = brands.filter((brand) => {
			return brand_slugs?.includes(brand.slug)
		})
		return brand_ids ? brand_ids.map((brand) => brand.option_id) : undefined
	}
	return undefined
}

/**
 * Get categories URL parameter to filter products
 *
 * TODO: use getURLParams instead
 */
export function getCatsParam(url: URL, param_name: string, subcats: Array<Category>) {
	const value = url.searchParams.get(param_name)
	if (value) {
		const cat_slugs = value?.split(',')
		const cat_ids = subcats.filter((cat) => {
			const cat_path = cat.url_path.split('/').slice(-1)
			const cat_slug = cat_path[cat_path.length - 1]

			return cat_slugs?.includes(cat_slug)
		})
		return cat_ids ? cat_ids.map((cat) => cat.id) : undefined
	}
	return undefined
}

export async function getRequestJSON<T>(request: Request, schema?: Schema<T>, validate_options?: ValidateOptions): Promise<T>;

/**
 * Equivalent of `request.json()` that returns a 400 to the client if the JSON is malformed.
 *
 * @param request
 * @param schema schema of the payload. The function returns a 400 with the error message if the schema validation fails
 * @param validate_options options passed to `schema.validate()`
 */
export async function getRequestJSON(request: Request, schema?: Schema, validate_options?: ValidateOptions) {
	let payload: unknown
	try {
		payload = await request.json()
	} catch (SyntaxError) {
		console.log("SyntaxError", SyntaxError)
		console.error(SyntaxError)
		error(400, 'Malformed JSON')
	}

	if (schema !== undefined) {
		try {
			return await schema.validate(payload, validate_options)
		} catch (e) {
			error(400, `Invalid data: ${e}`)
		}
	}

	return payload
}

/**
 * Equivalent of ``getRequestJSON`` for URL parameters.
 * Validate and coerce parameters according to a schema.
 * Raise a 400 if the parameters don't match the schema.
 */
export async function getURLParams<T>(url: URL, schema: Schema<T>, validate_options?: ValidateOptions): Promise<T> {
	const params: Record<string, string> = {}
	url.searchParams.forEach((value, key) => {
		if (value !== "") {
			params[key] = value
		}
	})

	try {
		return await schema.validate(params, validate_options)
	} catch (e) {
		error(400, `Invalid data: ${e}`)
	}
}